/* Native scrolling; pacing and accordion transitions mirror the reference file. */
(() => {
  'use strict';
  const clamp = value => Math.max(0, Math.min(1, value));
  const phaseAt = progress => Math.min(3, Math.floor(clamp(progress) * 4));
  const distanceFor = (viewport, totalVh = 480) => viewport * (Math.max(480, Math.min(1000, totalVh)) / 100 - 1);
  if (typeof module === 'object' && module.exports) module.exports = {phaseAt, distanceFor};
  if (typeof window === 'undefined') return;
  const instances = new Map();

  const create = root => {
    const items = [...root.querySelectorAll('.cue-memory__item')];
    const scenes = [...root.querySelectorAll('.cue-memory__scene')];
    const visual = root.querySelector('.cue-memory__visual');
    const stage = root.querySelector('.cue-memory__stage');
    const dots = root.querySelector('.cue-memory__dots');
    if (items.length !== 4 || scenes.length !== 4 || !visual || !stage || !dots) return null;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const cleanups = [];
    const on = (target, event, callback, options) => {
      target.addEventListener(event, callback, options);
      cleanups.push(() => target.removeEventListener(event, callback, options));
    };
    let active = -1, start = 0, distance = 0, pinned = false, frame = 0, timer = 0, pending = null;
    let measuring = false, destroyed = false;
    // Decode the four final illustrations shortly before their sticky chapter.
    let imagesWarmed = false, imageObserver;
    const warmImages = () => {
      if (imagesWarmed || destroyed) return;
      imagesWarmed = true;
      imageObserver?.disconnect();
      visual.querySelectorAll('img').forEach(image => {
        image.loading = 'eager';
        if (typeof image.decode === 'function') image.decode().catch(() => {});
      });
    };
    if ('IntersectionObserver' in window) {
      imageObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) warmImages();
      }, {rootMargin: '700px 0px', threshold: 0});
      imageObserver.observe(root);
      cleanups.push(() => imageObserver.disconnect());
    } else warmImages();
    const range = document.createElement('input');
    range.type = 'range'; range.min = '0'; range.max = '3'; range.step = '1'; range.value = '0';
    range.className = 'cue-memory__range';
    range.setAttribute('aria-label', 'Memory feature');
    range.setAttribute('aria-orientation', 'vertical');
    const buttons = items.map((item, index) => {
      const button = document.createElement('span');
      button.className = 'cue-memory__dot';
      button.setAttribute('aria-hidden', 'true');
      dots.append(button);
      on(item.querySelector('.cue-memory__label'), 'click', () => go(index));
      return button;
    });
    dots.append(range);
    on(range, 'input', () => go(Number(range.value)));
    const set = index => {
      if (index === active) return;
      active = index;
      items.forEach((item, i) => {
        const selected = i === index;
        item.classList.toggle('active', selected);
        item.querySelector('.cue-memory__label').setAttribute('aria-expanded', String(selected));
        item.querySelector('.cue-memory__detail').inert = !selected;
        scenes[i].classList.toggle('active', selected);
        scenes[i].inert = !selected;
        scenes[i].setAttribute('aria-hidden', String(!selected));
        buttons[i].setAttribute('aria-current', String(selected));
      });
      visual.dataset.scene = String(index);
      range.value = String(index);
      range.setAttribute('aria-valuetext', items[index].querySelector('.cue-memory__label').textContent.trim());
    };
    const update = () => {
      frame = 0;
      if (destroyed || measuring || !pinned) return;
      if (pending && (Math.abs(scrollY - pending.top) < 2 || performance.now() > pending.until)) pending = null;
      if (!pending) set(phaseAt((scrollY - start) / distance));
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const go = index => {
      set(Math.max(0, Math.min(3, index)));
      if (!pinned) return;
      const top = start + distance / 4 * (active + .12);
      pending = {top, until: performance.now() + 1800};
      window.scrollTo({top, behavior: motion.matches ? 'instant' : 'smooth'});
    };
    const layout = () => {
      timer = 0;
      if (destroyed) return;
      const oldY = scrollY;
      const preserve = pinned && oldY >= start && oldY <= start + distance && !pending;
      const progress = distance ? clamp((oldY - start) / distance) : 0;
      const previous = Math.max(0, active);
      const header = document.getElementById('site-header')?.offsetHeight || 0;
      const available = innerHeight - header;
      measuring = true;
      root.classList.add('memory-measuring');
      root.style.setProperty('--memory-header', `${header}px`);
      root.style.setProperty('--memory-stage-height', `${available}px`);
      root.style.setProperty('--memory-travel', '0px');
      pinned = !motion.matches && available >= 420 && CSS.supports('position', 'sticky');
      root.classList.toggle('memory-pinned', pinned);
      if (pinned) {
        for (let i = 0; i < 4; i++) {
          set(i);
          const height = Math.max(stage.scrollHeight, stage.getBoundingClientRect().height);
          const copy = root.querySelector('.cue-memory__copy');
          const detail = items[i].querySelector('.cue-memory__detail>div');
          if (height > available - 8 || copy.scrollHeight > copy.clientHeight + 2 || detail.scrollHeight > detail.clientHeight + 2) pinned = false;
        }
      }
      set(previous);
      distance = pinned ? distanceFor(innerHeight, Number(root.dataset.scrollVh) || 480) : 0;
      root.classList.toggle('memory-pinned', pinned);
      root.style.setProperty('--memory-travel', `${distance}px`);
      start = scrollY + root.getBoundingClientRect().top - header;
      root.classList.remove('memory-measuring');
      measuring = false;
      if (!pinned) pending = null;
      if (preserve && pinned && !motion.matches) {
        const nextY = start + progress * distance;
        if (Math.abs(nextY - oldY) > 1) scrollTo({top: nextY, behavior: 'instant'});
      }
      update();
      document.dispatchEvent(new CustomEvent('cue:scene-layout'));
    };
    const scheduleLayout = () => { clearTimeout(timer); timer = setTimeout(layout, 120); };
    const release = () => { pending = null; };
    on(window, 'scroll', schedule, {passive: true});
    on(window, 'resize', scheduleLayout, {passive: true});
    on(window, 'orientationchange', scheduleLayout, {passive: true});
    on(window, 'pageshow', scheduleLayout);
    on(window, 'load', scheduleLayout);
    on(document, 'shopify:section:reorder', scheduleLayout);
    on(document, 'shopify:section:load', scheduleLayout);
    on(document, 'shopify:section:unload', scheduleLayout);
    on(window, 'wheel', release, {passive: true});
    on(window, 'touchstart', release, {passive: true});
    on(window, 'keydown', event => {
      if (/INPUT|TEXTAREA|SELECT/.test(event.target.tagName) || event.target.isContentEditable || event.altKey || event.ctrlKey || event.metaKey) return;
      if (['ArrowDown','ArrowUp','PageDown','PageUp','Home','End',' '].includes(event.key)) release();
      if (!root.contains(document.activeElement) && !(pinned && scrollY >= start && scrollY <= start + distance)) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault(); go(active + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    if (motion.addEventListener) on(motion, 'change', scheduleLayout);
    else { motion.addListener(scheduleLayout); cleanups.push(() => motion.removeListener(scheduleLayout)); }
    if (document.fonts) document.fonts.ready.then(() => { if (!destroyed) scheduleLayout(); });
    if ('ResizeObserver' in window) {
      let size = '';
      const observer = new ResizeObserver(entries => {
        const box = entries[0].contentRect;
        const next = `${Math.round(box.width)}:${Math.round(box.height)}`;
        if (next !== size) { size = next; scheduleLayout(); }
      });
      observer.observe(document.body);
      cleanups.push(() => observer.disconnect());
    }
    root.classList.add('memory-ready');
    set(0); layout();
    return {
      select: go,
      destroy() {
        destroyed = true;
        clearTimeout(timer); cancelAnimationFrame(frame);
        cleanups.forEach(fn => fn()); buttons.forEach(button => button.remove()); range.remove();
        root.classList.remove('memory-ready', 'memory-pinned');
        root.style.removeProperty('--memory-travel');
      }
    };
  };
  const mount = () => {
    instances.forEach((instance, element) => { if (!element.isConnected) { instance.destroy(); instances.delete(element); } });
    document.querySelectorAll('[data-cue-memory]').forEach(element => {
      if (!instances.has(element)) { const instance = create(element); if (instance) instances.set(element, instance); }
    });
  };
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, element) => { if (event.target.contains(element)) { instance.destroy(); instances.delete(element); } });
  });
  document.addEventListener('shopify:block:select', event => {
    const item = event.target.closest('[data-memory-phase]');
    if (item) instances.get(item.closest('[data-cue-memory]'))?.select(Number(item.dataset.memoryPhase));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, {once:true}); else mount();
})();
